import React, { useEffect, useState } from 'react';
import { styled } from '@this/constants/themes';
import { calculateAge } from '@this/domain/age_util';
import type TransportElement from '@this/domain/transport_element2';
import type TravelerInformation from '@this/src/domain/traveler_information';
import moment from 'moment';
import type { Moment } from '@this/src/lib/moment';
import { observer } from 'mobx-react';
import { FormField, InputGroup, TextInput } from './order_item_traveler.style';

type Props = {
  travelerInformation: TravelerInformation;
  transportDate: Moment | null;
  classNameForModifiedField: (...path: (string | number)[]) => string;
  transportType: TransportElement['transportType'] | null;
  onAnyChange?: () => void;
};

const OrderItemTravelerInputsFormComponent: React.FC<Props> = ({
  travelerInformation,
  transportDate,
  classNameForModifiedField,
  transportType,
  onAnyChange
}) => {
  const [birthdayInput, setBirthdayInput] = useState(travelerInformation.birthday?.format('YYYY-MM-DD') ?? '');

  useEffect(() => {
    const formatted = travelerInformation.birthday?.format('YYYY-MM-DD') ?? '';
    setBirthdayInput(formatted);
  }, [travelerInformation.birthday]);

  const handleBirthdayChange = (value: string) => {
    setBirthdayInput(value);
    onAnyChange?.();
  };

  const handleBirthdayBlur = () => {
    if (birthdayInput === '') {
      travelerInformation.birthday = undefined;
      travelerInformation.flightBirthday = undefined;
      return;
    }

    const parsed = moment(birthdayInput, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      travelerInformation.birthday = parsed;
      travelerInformation.flightBirthday = parsed;
      setBirthdayInput(parsed.format('YYYY-MM-DD'));
      onAnyChange?.();
      return;
    }

    travelerInformation.birthday = undefined;
    travelerInformation.flightBirthday = undefined;
    onAnyChange?.();
  };

  return (
    <div>
      <FormField>
        <label
          className={`
            ${classNameForModifiedField('lastNameRoman')}
            ${classNameForModifiedField('firstNameRoman')}
          `}
        >
          氏名(ローマ字)
        </label>
        <InputGroup>
          {transportType === 'foreign_air' ? (
            <>
              <NameInputShort
                type="text"
                placeholder="姓(ローマ字)"
                value={travelerInformation.lastNameRoman}
                onChange={e => {
                  travelerInformation.lastNameRoman = e.target.value;
                  onAnyChange?.();
                }}
              />
              <NameInputShort
                type="text"
                placeholder="ミドルネーム(ローマ字)"
                value={travelerInformation.middleNameRoman}
                onChange={e => {
                  travelerInformation.middleNameRoman = e.target.value;
                  onAnyChange?.();
                }}
              />
              <NameInputShort
                type="text"
                placeholder="名(ローマ字)"
                value={travelerInformation.firstNameRoman}
                onChange={e => {
                  travelerInformation.firstNameRoman = e.target.value;
                  onAnyChange?.();
                }}
              />
            </>
          ) : (
            <>
              <NameInput
                type="text"
                placeholder="姓(ローマ字)"
                value={travelerInformation.lastNameRoman}
                onChange={e => {
                  travelerInformation.lastNameRoman = e.target.value;
                }}
              />

              <NameInput
                type="text"
                placeholder="名(ローマ字)"
                value={travelerInformation.firstNameRoman}
                onChange={e => {
                  travelerInformation.firstNameRoman = e.target.value;
                }}
              />
            </>
          )}
        </InputGroup>
      </FormField>
      <FormField>
        <label
          className={`
            ${classNameForModifiedField('lastNameKana')}
            ${classNameForModifiedField('firstNameKana')}
          `}
        >
          氏名(カナ)
        </label>
        <InputGroup>
          <NameInput
            type="text"
            placeholder="姓(カナ)"
            value={travelerInformation.lastNameKana}
            onChange={e => {
              travelerInformation.lastNameKana = e.target.value;
            }}
          />
          <NameInput
            type="text"
            placeholder="名(カナ)"
            value={travelerInformation.firstNameKana}
            onChange={e => {
              travelerInformation.firstNameKana = e.target.value;
            }}
          />
        </InputGroup>
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('birthday')}>誕生日</label>
        <TextInput
          type="text"
          placeholder="例) 2020-04-01"
          value={birthdayInput}
          onChange={e => handleBirthdayChange(e.target.value)}
          onBlur={handleBirthdayBlur}
        />
      </FormField>
      <FlightAge>
        <label>搭乗時年齢</label>
        {travelerInformation.birthday && transportDate && (
          <span>{`${calculateAge(travelerInformation.birthday, transportDate).toString()}歳`}</span>
        )}
      </FlightAge>
      <FormField>
        <label className={classNameForModifiedField('gender')}>性別</label>
        <InputGroup>
          <label>
            <input
              type="radio"
              value="male"
              name="gender"
              checked={travelerInformation.flightGender === 'male'}
              onChange={e => {
                travelerInformation.flightGender = 'male';
              }}
            />
            男性
          </label>
          <label>
            <input
              type="radio"
              value="female"
              name="gender"
              checked={travelerInformation.flightGender === 'female'}
              onChange={e => {
                travelerInformation.flightGender = 'female';
              }}
            />
            女性
          </label>
        </InputGroup>
      </FormField>
    </div>
  );
};

export const NameInput = styled(TextInput)`
  width: 110px !important;
`;

export const NameInputShort = styled(TextInput)`
  width: 70px !important;
`;

const FlightAge = styled.div`
  margin-bottom: 0.75em;
`;

const OrderItemTravelerInputsForm = observer(OrderItemTravelerInputsFormComponent);
export default OrderItemTravelerInputsForm;
